import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";
import AsyncSelect from "../../../../../../../components/AsyncSelect";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../components/Flex";
import LoadingOverlay from "@/components/LoadingOverlay";
import StyledDialog from "../../../../../../../components/StyledDialog";
import SignatureInput from "../../../../../../../components/signiture/SignatureInput";
import { useSignatureController } from "../../../../../../../components/signiture/use-signature-controller";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useEncounterSignOff } from "../../../../../../../queries/encounter/use-encounter-summery-sign-off";
import { useUpdateAppointmentStatus } from "../../../../../../../queries/use-change-appointment-status";
import { useProviderListV2 } from "../../../../../../../queries/use-provider-list-v2";
import {
  DEF_PAGINATION,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useGetSignatureByProviderID } from "../../../../../../../queries/signature/use-get-signature-by-providerID";
import WithInfo from "../../../../../../../components/WithInfo";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import ViewEncounterSummaryDrawer from "../../../../billings/encounter/ViewEncounterSummaryDrawer";
import ConfirmationModal from "../../../../../../../components/app/ConfirmationModal";
import { RootState, store } from "../../../../../../../redux/store";
import { closeAndResetJoinRoom } from "../../../../../../../redux/feature/join-room/JoinRoomSlice";
import { ZoomContext } from "../../../../../../../zoom/zoom-context";
interface SignAndLockDrawerProps {
  disclosureHost: DisclosureHost;
  prefillSummeryData?: any;
  navigateWithoutGuard?: (callback: () => void) => void;
}

type SignFormik = {
  provider?: { value: string; label: string };
  providerNote?: string;
  providerSignature?: string;
  signerType?: "self" | "other";
};

const initialValues = {
  providerNote: "",
  providerSignature: "",
  signerType: "self" as "self" | "other",
};

const validationSchema = Yup.object().shape({
  provider: Yup.object().required().label("Provider"),
});

const SignAndLockDrawer = ({
  disclosureHost,
  prefillSummeryData,
  navigateWithoutGuard,
}: SignAndLockDrawerProps) => {
  const [k, setK] = useState<boolean>(true);
  /* Blocks the screen while the sign-off is in flight so the provider cannot
     submit the encounter more than once. */
  const [isSigningOff, setIsSigningOff] = useState<boolean>(false);
  const { canvasRef, signPad, isSignEmpty } = useSignatureController(
    disclosureHost.opened && k
  );
  const { mutateAsync: signOffEncounter } = useEncounterSignOff();
  const { mutateAsync: updateAppointmentStatus } = useUpdateAppointmentStatus();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const user = sessionStorage.getItem("user");
  const userData = user ? JSON.parse(user) : null;
  const navigate = useNavigate();
  const appointmentId = useQueryParams("appointmentid");
  const zmClient = useContext(ZoomContext);
  /* A live telehealth (Zoom) call is open globally when joinRoom.opened is true. */
  const isTelehealthCallActive = useSelector(
    (state: RootState) => state.joinRoom?.opened
  );
  const encounterSummaryDisclosure = useDisclosure(false, {
    onClose: () => {
      navigateWithoutGuard?.(() => {
        navigate("/app/provider/scheduling/appointment");
      });

      disclosureHost.close();

      // Callers that stay on their screen keep their URL; the next sign-off reads appointmentid from it.
      // if (!navigateWithoutGuard) return;

      /* Replace current history entry to prevent back button navigation */
      window.history.replaceState(
        null,
        "",
        "/app/provider/scheduling/appointment"
      );
    },
  });

  const { data: signatureData } = useGetSignatureByProviderID({
    providerUuid: userData?.uuid,
    xTENANTID: getXtenantId(),
  });

  const signOffHandler = async (v: SignFormik) => {
    /**
     * Block sign-off when a telehealth call is active but the patient has not
     * joined the visit virtually. getAllUser() includes self, so length > 1
     * means the patient joined; length <= 1 means the patient never joined.
     * Signing off in that state would push the visit into No Show after the
     * call ends, leaving a signed encounter that is also marked No Show.
     */
    if (isTelehealthCallActive) {
      const patientJoined = (zmClient?.getAllUser()?.length ?? 0) > 1;
      if (!patientJoined) {
        showSnackbar({
          message:
            "The patient has not joined the visit. You cannot sign off the encounter until the patient joins the call.",
          type: "error",
        });
        return;
      }
    }

    // Todo: need to check if the encounter is broken or not and remove comment
    if (!prefillSummeryData) {
      showSnackbar({
        message: "Visit is Broken, Please contact support.",
        type: "error",
      });
      return;
    }

    if (v.signerType === "self" && isSignEmpty()) {
      showSnackbar({
        message: "You must sign before submitting.",
        type: "error",
      });
      return;
    }

    const payload = {
      encounterSummaryId: prefillSummeryData?.uuid,
      encounterSignOff: {
        provider: v?.provider?.value,
        providerNote: v.providerNote,
        providerSignature:
          v.signerType === "self" ? signPad?.toDataURL() : undefined,
      },
      xTENANTID: getXtenantId(),
    };

    /* Keep the overlay up past the API call and the refetch, so it only lifts
       once the encounter summary (PDF) is on screen. */
    setIsSigningOff(true);
    try {
      const res = await signOffEncounter(payload);
      showSnackbar(getSuccessSnackbar(res));

      await queryClient.invalidateQueries({
        queryKey: ["encounter-summery-v2", appointmentId],
      });

      if (v.signerType === "self") {
        encounterSummaryDisclosure.open(appointmentId);
      } else {
        disclosureHost.close();
      }
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    } finally {
      setIsSigningOff(false);
    }
  };

  const callDisconnectDisclosure = useDisclosure(false, {
    onClose: () => {
      encounterSummaryDisclosure.close();
    },
  });

  /* Disconnect the active Zoom call, then close the summary and navigate away. */
  const handleDisconnectCall = async () => {
    try {
      await zmClient?.leave();
    } catch (err) {
      console.error("Failed to leave Zoom call:", err);
    }
    store.dispatch(closeAndResetJoinRoom());
    callDisconnectDisclosure.close();
    encounterSummaryDisclosure.close();
  };

  const noShowDisclosure = useDisclosure(false);

  /**
   * Patient never joined: mark the appointment as No Show, disconnect the
   * call, then close the summary and navigate away.
   */
  const handleNoShowDisconnect = async () => {
    const noShowAppointmentId =
      store.getState().joinRoom.appointmentId?.toString() ||
      appointmentId ||
      "";
    try {
      await updateAppointmentStatus({
        reason: "",
        appointmentId: noShowAppointmentId,
        status: "NO_SHOW",
        xTENANTID: getXtenantId(),
      });
    } catch (err) {
      console.error("Failed to mark appointment as No Show:", err);
      /**
       * The encounter is already signed off at this point, so the backend
       * rejects the status change. Disconnecting the call must still end
       * gracefully, so log it instead of surfacing an error to the provider.
       */
      // showSnackbar(getErrorSnackbar(err));
    }
    try {
      await zmClient?.leave();
    } catch (err) {
      console.error("Failed to leave Zoom call:", err);
    }
    store.dispatch(closeAndResetJoinRoom());
    noShowDisclosure.close();
    encounterSummaryDisclosure.close();
  };

  /**
   * Invoked when the provider closes the encounter summary. If the telehealth
   * call is still active, intercept the close and prompt to disconnect first
   * (the summary stays open behind the modal). getAllUser() includes self, so
   * length > 1 means both joined; length <= 1 means the patient never joined.
   * When no call is active, close the summary and navigate away as usual.
   */
  const handleSummaryClose = () => {
    if (!isTelehealthCallActive) {
      encounterSummaryDisclosure.close();
      return;
    }

    const bothParticipantsJoined = (zmClient?.getAllUser()?.length ?? 0) > 1;
    if (bothParticipantsJoined) {
      callDisconnectDisclosure.open();
    } else {
      noShowDisclosure.open();
    }
  };

  const [searchProvider, setSearchProvider] = useDebounce<string>("");
  const formik = useFormik<SignFormik>({
    initialValues,
    validationSchema,
    onSubmit: signOffHandler,
  });

  useEffect(() => {
    if (disclosureHost.opened) {
      formik.setFieldValue("provider", {
        value: userData?.uuid,
        label: `${userData?.firstName || ""} ${userData?.lastName || ""}`,
      });
    }
  }, [disclosureHost.opened]);

  useEffect(() => {
    if (formik.values.signerType === "self" && userData?.uuid) {
      formik.setFieldValue("provider", {
        value: userData?.uuid,
        label: `${userData?.firstName || ""} ${userData?.lastName || ""}`,
      });
    }
  }, [formik.values.signerType]);

  useEffect(() => {
    if (formik.values.signerType === "other" && !isSignEmpty()) {
      signPad?.clear();
    }
  }, [formik.values.signerType]);

  const { data: providersList } = useProviderListV2({
    pagination: DEF_PAGINATION,
    searchString: searchProvider,
    xTENANTID: getXtenantId(),
  });

  return (
    <StyledDialog
      onClose={disclosureHost.close}
      open={disclosureHost.opened}
      dialogTitle={"Sign And Lock Diagnosis And Treatment Plan"}
      dialogAction={
        <Button
          onClick={formik.submitForm}
          variant="contained"
          disabled={isSigningOff}
          style={{ width: "120px" }}
        >
          {formik.values.signerType === "self" ? "Sign & Lock" : "Assign Task"}
        </Button>
      }
    >
      <ViewEncounterSummaryDrawer
        disclosure={encounterSummaryDisclosure}
        onClose={handleSummaryClose}
      />
      <ConfirmationModal
        disclosureHost={callDisconnectDisclosure}
        title="Encounter Completed"
        content="Consultation has been completed. Please disconnect the call before leaving the encounter."
        confirmationText="Disconnect Call"
        cancelText="Continue Call"
        onConfirm={handleDisconnectCall}
      />
      {noShowDisclosure.opened && (
        <ConfirmationModal
          disclosureHost={noShowDisclosure}
          title="Encounter Completed"
          confirmationText="Disconnect Call"
          cancelText="Continue Call"
          content={
            "The patient did not join the call. The encounter will be moved to No Show. Please disconnect the call to continue."
          }
          onConfirm={handleNoShowDisconnect}
        />
      )}
      <LoadingOverlay isLoading={isSigningOff} />
      <Flex flexDirection={"column"} width={"40vw"}>
        <Flex width={"100%"} gap={2}>
          <Flex flexDirection={"column"} width="50%" gap={2}>
            <Box
              sx={{
                mt: 4,
              }}
            >
              <FormControl component="fieldset">
                <FormLabel component="legend">Signed By</FormLabel>
                <RadioGroup
                  row
                  value={formik.values.signerType || "self"}
                  onChange={(e) =>
                    formik.setFieldValue("signerType", e.target.value)
                  }
                  name="signerType"
                >
                  <FormControlLabel
                    value="self"
                    control={<Radio />}
                    label="Self"
                  />
                  {/*note: changes as per B.A [bhushan] <WithInfo title="Signoff Task will be assigned to the selected user from dropdowns.">
                    <FormControlLabel
                      value="other"
                      control={<Radio />}
                      label="Other"
                    />
                  </WithInfo> */}
                </RadioGroup>
              </FormControl>
            </Box>
            <Box>
              <AsyncSelect
                label="Name"
                required
                name="provider"
                handleBlur={formik.handleBlur}
                error={formik.submitCount > 0 && formik.errors?.provider}
                value={formik.values?.provider}
                placeholder="Search And Select Provider "
                onSearch={setSearchProvider}
                options={providersList?.data?.content?.map((it) => {
                  return {
                    value: it?.uuid,
                    label: it?.name,
                  };
                })}
                onChange={(val) => formik.setFieldValue("provider", val)}
                disabled={formik.values.signerType === "self"}
              />
            </Box>
            <Box>
              <CustomTextArea
                label="Note"
                id="outlined-multiline-static"
                rows={12}
                variant="outlined"
                name="providerNote"
                placeholder="Note"
                onBlur={formik.handleBlur}
                value={formik.values?.providerNote}
                onChange={formik.handleChange}
              />
              <Flex justifyContent="flex-end" mt={1}>
                <WithInfo
                  hideInfoIcon={(signatureData?.data as any)?.providerSignature}
                  title="Signature is not available for this provider. Please sign the signature manually to get import signature feature"
                >
                  <Button
                    disabled={
                      !(signatureData?.data as any)?.providerSignature ||
                      formik.values.signerType === "other"
                    }
                    variant="outlined"
                    startIcon={<ArrowForwardIcon />}
                    sx={{ height: "34px" }}
                    onClick={() => {
                      signPad?.fromDataURL(
                        (signatureData?.data as any).providerSignature
                      );
                    }}
                  >
                    Import Signature
                  </Button>
                </WithInfo>
              </Flex>
            </Box>
          </Flex>
          <Box
            sx={{
              width: "50%",
              position: "relative",
            }}
          >
            <SignatureInput
              disabled={formik.values.signerType === "other"}
              required={formik.values.signerType === "self"}
              error={
                formik.submitCount > 0 &&
                formik.values.signerType === "self" &&
                isSignEmpty()
              }
              label="Signature"
              canvasRef={canvasRef}
              w="390px"
              h="330px"
            />
            <Button
              variant="contained"
              sx={{
                position: "absolute",
                bottom: 10,
                right: 10,
                height: "24px",
              }}
              onClick={() => {
                setK((prev) => !prev);
                signPad?.clear();
              }}
              disabled={formik.values.signerType === "other"}
            >
              clear
            </Button>
          </Box>
        </Flex>
      </Flex>
    </StyledDialog>
  );
};

export default SignAndLockDrawer;
